/**
 * Esta função especializada faz fetch de envolvidos usando múltiplas estratégias
 * para contornar o erro 401 Unauthorized
 * 
 * @param {Function} debugLog - Função para logging (opcional)
 * @param {Function} showMessage - Função para exibir mensagens (opcional)
 * @returns {Promise<{success: boolean, data: Array, message: string}>}
 */
export const fetchEnvolvidos = async (debugLog = console.log, showMessage = () => {}) => {
  const token = localStorage.getItem('token');
  if (!token) {
    debugLog('Token não encontrado no localStorage');
    showMessage('Sessão expirada. Por favor, faça login novamente.', 'warning');
    return { success: false, data: [], message: 'Token não encontrado' };
  }

  const endpoints = [
    '/envolvidos/with-processes',
    '/envolvidos/with-processos',
    '/envolvidos?include_processos=true',
    '/envolvidos'
  ];

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const authFormats = [
    `Bearer ${token}`,
    `Token ${token}`,
    token
  ];
  
  for (const endpoint of endpoints) {
    for (const authHeader of authFormats) {
      try {
        debugLog(`Tentando endpoint ${endpoint} com formato de autenticação: ${authHeader.substring(0, 6)}...`);
        
        const response = await fetch(`${baseUrl}${endpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': authHeader
          },
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          debugLog('Dados recebidos com sucesso:', data);
          
          const envolvidos = data?.envolvidos ? data.envolvidos : 
                             Array.isArray(data) ? data : [];
                             
          if (envolvidos.length > 0) {
            debugLog(`Sucesso com endpoint ${endpoint} e formato de autenticação ${authHeader.substring(0, 6)}...`);
            return { 
              success: true, 
              data: envolvidos, 
              message: `${envolvidos.length} envolvidos carregados` 
            };
          }
        } else {
          debugLog(`Falha no endpoint ${endpoint} com formato ${authHeader.substring(0, 6)}...: ${response.status}`);
        }
      } catch (error) {
        debugLog(`Erro ao acessar ${endpoint} com formato ${authHeader.substring(0, 6)}...:`, error);
      }
    }
  }
  
  return { 
    success: false, 
    data: [], 
    message: 'Não foi possível carregar os envolvidos após múltiplas tentativas' 
  };
};

/**
 * Função para processar dados de envolvidos e normalizar o formato dos processos e marcas
 * 
 * @param {Array} envolvidos - Array de envolvidos da API
 * @param {Function} normalizeEnvolvidoData - Função para normalizar dados do envolvido
 * @returns {Object} Dados normalizados e cache de processos
 */
export const processEnvolvidosData = (envolvidos, normalizeEnvolvidoData) => {
  const dadosNormalizados = envolvidos.map(normalizeEnvolvidoData || (e => e));
  
  const cache = {};
  envolvidos.forEach(env => {
    if (Array.isArray(env.processos) && env.processos.length > 0) {
      cache[env.id] = env.processos.map(processo => {
        let marcaNome = '-';
        
        if (Array.isArray(processo.marca) && processo.marca.length > 0) {
          marcaNome = processo.marca[0].nome || '-';
        } else if (processo.marca && typeof processo.marca === 'object') {
          marcaNome = processo.marca.nome || '-';
        } else if (typeof processo.marca === 'string') {
          marcaNome = processo.marca;
        }
        
        return {
          ...processo,
          marca: { nome: marcaNome },
          situacao: processo.situacao || 'Pendente'
        };
      });
    } else {
      cache[env.id] = [];
    }
  });
  
  return {
    dadosNormalizados,
    cache
  };
};
